import { TranslationSchema } from "../types/translations";
import { languageStringsAdmin } from "./en/admin";
import { languageStringsCampaign } from "./en/campaign";
import { languageStringsContact } from "./en/contact";
import { languageStringsPopups } from "./en/popup";
import { languageStringsSubscriptions } from "./en/subscriptions";

export const en: TranslationSchema = {
  sidePanel: {
    campaign: "Campaign",
    connection: "Integration",
    settings: "Settings",
    infinite: "Unlimited",
  },
  noTariffBanner: {
    title: "Your account does not have an active plan",
    description:
      "Without an active plan, we cannot display notifications on your website and increase its conversion rate.",
    button1: "Activate plan",
    button2: "free for the first 14 days",
  },
  noPixelBanner: {
    title: "The pixel script is not embedded on the website.",
    description: "To enable notifications, please insert the pixel script.",
    button: "How to insert the pixel?",
  },
  extraCreditsPage: {
    buttonText: "Buy",
    shows: "views",
    navigationButtonText: "Buy more",
    purchaseError:
      "We couldn't start the payment. Please try again later or contact support.",
  },
  choose: "Choose",
  apiKeysTitle: "Integration - display reviews of satisfied customers",
  zboziApiChangeNotice: "Notice: The Zboží.cz API has been replaced by the new Sklik API. If you have a Zboží.cz integration, you need to update your API key – obtain a new refresh token at sklik.cz under Settings → API keys.",
  addingApiKeys: "Adding API Keys",
  optionalAddApiKey: "This step is optional. You can add API keys later.",
  monthlyCredits: "Remaining monthly credits",
  portableCredits: "Extra purchased credits",
  extraCredits: "Extra purchased credits",
  help: "Help",
  reportProblem: "Report a problem",
  saveChanges: "Save changes",
  reset: "Reset",
  defaultSettings: "Default settings",
  addKey: "Add key",
  login: "Log in",
  back: "Back",
  close: "Close",
  delete: "Delete",
  verified: "Verified",
  yes: "Yes",
  notVerified: "Not verified",
  connected: "Connected",
  active: "Active",
  inactive: "Inactive",
  check: "Check",
  add: "Add",
  edit: "Edit",
  save: "Save",
  continue: "Continue",
  userIsNotLoggedIn: "User is not logged in",
  deleteWebsite: "Delete website",
  spolehliveRecenze: "Reliable reviews",
  logout: "Log out",
  paymentFailed: {
    title: "Payment was cancelled",
    description: "The payment could not be completed.",
    continue: "Try again",
  },
  paymentRejected: {
    title: "Payment was rejected",
    description: "Payment was not accepted. Please try again.",
    continue: "Continue",
  },
  notFound: {
    title: "We didn't find the requested page",
    back: "Back",
  },
  loggedOut: {
    title: "You have been logged out",
    description: "You can now close the page.",
    close: "Close page",
  },
  passwordChange: {
    title: "Change password",
    setPasswordTitle: "Set password",
    oldPassword: "Current password",
    newPassword: "New password",
    password: "Password",
    confirmPassword: "Confirm new password",
    passwordMismatch: "Passwords do not match",
    passwordSet: "Password was set, please log in again.",
    passwordSetFailed: "Failed to set password",
    passwordChanged: "Password was changed, please log in again.",
    passwordChangeFailed: "Failed to change password",
    errors: {
      passwordsDoNotMatch: "Passwords do not match",
      failedToChange: "Failed to change password",
    },
    success: "Password was changed, please log in again.",
  },
  onBoarding: {
    title: "Add your first website",
    description:
      "Don't worry, we'll guide you step by step. Soon you'll be able to enjoy all the benefits that Verified Web offers.",
    start: "Start",
    logout: "Log out",
    errors: {
      websiteNameTaken: "Website with this name is already taken",
      fillAllFields: "Fill all fields",
      enterApiKey: "Enter API key",
      enterCredentials: "Enter username and password",
    },
    apiKeyModal: {
      added: "Added.",
      notVerified: "Not verified",
      zbozi: {
        noAccessToPremise: "The API key does not have access to this premise. Possible causes: (1) The key belongs to a different Sklik account than the one managing this premise. (2) The premise does not have active conversion measurement on Zboží.cz – this is required to use the reviews API. Set up conversion measurement at: zbozi.cz/mereni-konverzi",
        invalidApiKey: "The API key is invalid or has expired. Generate a new key in Settings → API keys in Sklik.",
        insufficientPermissions: "The API key has insufficient permissions. Create a new key in Sklik with permissions for Zboží.cz / Nákupy data.",
        verificationFailed: "Verification failed. Please check the API key and premise ID.",
      },
    },
  },
  resetPassword: {
    title: "Reset Password",
    reset: "Reset",
    emailSent: "If an account exists with this email, you will receive password reset instructions.",
    newPassword: "New Password",
    confirmPassword: "Confirm Password",
    submit: "Reset Password",
    backToLogin: "Back to Login",
    success: "Password Successfully Reset",
    successMessage: "Your password has been reset. All sessions have been logged out. You can now log in with your new password.",
    invalidLink: "Invalid Link",
    errorTitle: "Invalid Reset Link",
    errorMessage: "This link is invalid or has expired. Please request a new password reset link.",
    passwordTooShort: "Password must be at least 8 characters",
    passwordsMismatch: "Passwords do not match",
    genericError: "Failed to reset password. Please try again.",
    description: "Enter your new password",
    redirecting: "Redirecting to login",
  },
  deleteWeb: {
    title: "Delete website?",
    description: "All data and campaigns will be deleted.",
    delete: "Delete",
    close: "Close",
  },
  loginScreen: {
    title: "Login",
    email: "Email",
    password: "Password",
    button: "Log on",
    newUsers: "New user?",
    forgotPassword: "Forgot your password?",
    resetPassword: "Reset",
    signUp: "Sign up",
  },
  registrationScreen: {
    title: {
      first: "You're just one step away from",
      second: "increasing the conversion rate",
      third: "of your e-shop",
      fourth: "by up to 20%!",
    },
    subTitle: "Fill in the following details and join successful e-shop owners",
    name: "Name",
    phone: "Phone number (optional)",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm password",
    accept: "I agree with the",
    terms: "terms and conditions",
    button: "Complete registration",
    alreadyHaveAccount: "Already have an account?",
    login: "Log in",
  },
  addWebsite: {
    title: "Add website",
    name: "Website name",
    warning: "Enter the full URL of your website, including https.",
    includeHttps: "including https",
    url: "Website URL",
    notificationLanguage: "Notification Language",
    czech: "Czech",
    slovak: "Slovak",
    polish: "Polish",
  },
  howToInstallPixel: {
    title: "How to install the pixel",
    first: "Copy the Pixel code.",
    second: "Paste the code into the header of your website before </head>.",
    third:
      "Visit your website to activate the pixel code. Then verify it using the 'Check' button.",
    help: "How to insert the pixel code into your website?",
  },
  websites: {
    title: "View all",
    website: "Website",
    created: "Created",
    campaigns: "Campaigns",
    addWebsite: "Add website",
    connect: "Connect",
    connected: "Connected",
    yourWebsites: "Your websites",
    addButton: "Add",
    backButton: "Back",
  },
  iconSelect: {
    selectedIcon: "Selected Icon",
    selectIcon: "Click to select an icon",
    dialogTitle: "Choose an icon",
    dialogClose: "Close",
  },
  settingsTabs: {
    webSettings: "Basic settings",
    customLogo: "Custom logo",
  },
  customLogo: {
    showLogo: "Show logo",
    logo: "Logo",
    text: "Text",
    textColor: "Text color",
    save: "Save",
  },
  shoptetFirstTouchScreen: {
    title: "The add-on was successfully installed.",
    description1:
      "We have prepared a set of smart notifications for your e-shop that, based on our experience with similar shops, will most effectively increase your order volume. They will be activated automatically right after you start your free trial.",
    description2:
      "Click the button to activate smart notifications and just watch your order count grow each day.",
    button: "Start 14-day free trial",
    error: "Login failed. Please log out and try again.",
  },
  webarealFirstTouchScreen: {
    title: "The add-on was successfully installed.",
    description1:
      "We have prepared a set of smart notifications for your e-shop that, based on our experience with similar shops, will most effectively increase your order volume. They will be activated automatically right after you start your free trial.",
    description2:
      "Click the button to activate smart notifications and simply watch your order count grow each day.",
    button: "Start 14-day free trial",
    error: "Login failed. Please log out and try again.",
  },
  eshopRychleFirstTouchScreen: {
    title: "Welcome to Verified Web!",
    description1: "Your account has been successfully created.",
    description2: "Please set a password to access the administration.",
    button: "Set password",
    error: "Login failed. Please try again.",
  },
  upgatesFirstTouchScreen: {
    title: "The add-on was successfully installed.",
    description1:
      "We have prepared a set of smart notifications for your e-shop that, based on our experience with similar shops, will most effectively increase your number of orders. They will launch automatically right after you activate your free trial.",
    description2:
      "Click the button to activate smart notifications and simply watch your daily order count grow.",
    button: "Start 14-day free trial",
    error: "Login failed. Please log out and try again.",
  },
  shopifyFirstTouchScreen: {
    title: "App installation successful!",
    description1:
      "We have prepared a set of smart notifications for your Shopify store that, based on our experience with similar stores, will most effectively increase your order volume. They will be activated automatically right after you start your free trial.",
    description2:
      "Click the button to activate smart notifications and watch your order count grow each day.",
    button: "Start 14-day free trial",
    error: "Login failed. Please try again.",
  },
  trustPilot: {
    businessUnitId: "Business Unit ID",
    apiKey: "API Key",
    apiSecret: "API Secret",
    reviews: "reviews",
    reviewsWithRating: "Reviews with more than",
    stars: "stars",
    maxReviews: "Minimum reviews to display",
    verificationPrompt:
      "Please verify your TrustPilot API Key and Business Unit ID",
    sourceUrlAddress: "Enter your Business Unit ID",
  },
  errors: {
    pixelCheckFailed: "Automatic pixel check was not successful.",
    placesApiError: "An error occurred while loading data from Google Maps. Please contact us for assistance.",
    userNotFound: "User with this email does not exist",
    userAlreadyExists: "User with this email already exists",
    fillAllFields: "Please fill in all required fields",
    passwordsDoNotMatch: "Passwords do not match",
    termsRequired: "You must agree to the terms and conditions",
    invalidEmail: "Please enter a valid email",
    logoFileTooLarge: "Logo is too large. Maximum size is {{maxSize}} KB, your file is {{actualSize}} KB.",
    logoFileTooLargeSimple: "Logo is too large. Maximum file size is 100 KB.",
    failedToLoadSettings: "Failed to load settings",
    // Backend error types
    Generic: "An unexpected error occurred",
    Unauthorized: "You are not authorized to perform this action",
    Unhandled: "Unhandled error",
    NotEnoughCredits: "You don't have enough credits",
    IntegrityHashNotFound: "Session expired, please log in again",
    ErrorVerifyingAccessToken: "Error verifying access token",
    MissingAccessToken: "Access token is missing",
    AccessTokenExpired: "Access token has expired",
    InvalidCredentials: "Invalid credentials",
    UserEmailAlreadyExists: "User with this email already exists",
    EmailNotSent: "Failed to send email, please try again later",
    ItemNotFound: "Item not found",
    WebsiteNotFound: "Website not found",
    UserNotFound: "User not found",
    CampaignNotFound: "Campaign not found",
    NotificationSettingsNotFound: "Notification settings not found",
    ParameterBadFormat: "Invalid parameter format",
    QueryStringMissing: "Missing URL parameter",
    BodyParameterMissing: "Missing request parameter",
    EnvVarNotFound: "Configuration variable missing",
    PricingPlanNotFoundInStripe: "Pricing plan not found in Stripe",
    PricingPlanInconsistencyInStripe: "Pricing plan inconsistency in Stripe",
    PriceNotFoundInStripe: "Price not found in Stripe",
    CustomerNotFoundInStripe: "Customer not found in Stripe",
    SubscriptionInconsistencyInStripe: "Subscription inconsistency in Stripe",
    SubscriptionNotFoundInStripe: "Subscription not found in Stripe",
    PricingPlanCreditsNotFound: "Pricing plan credits not configured",
    StripeWebhookError: "Stripe webhook error",
    CustomerDeletedInStripe: "Customer is deleted in Stripe",
    UnexpectedStripeInvoicePayload: "Unexpected data from Stripe",
    StripeSessionUrlError: "Stripe payment session URL error",
    ThirdPartyApiError: "External API error",
    UserWithoutPasswordError: "User does not have a password set, likely registered through an external system",
    PrivateKeyDoesNotMatch: "Private key does not match",
    UserNotDeletedInStripe: "User was not deleted from Stripe",
    InvalidRequestBody: "Invalid request format",
    UserNotRegisteredThroughWebareal: "User not registered through Webareal",
    WebarealWebsiteAlreadyExists: "This Webareal e-shop is already registered",
    UserNotRegisteredThroughUpgates: "User not registered through Upgates",
    RedisClientNotFound: "Redis client not found",
    PayloadTooLarge: "Request is too large",
  },
  cancelSubscription: {
    title: "Are you sure you want to cancel the subscription renewal?",
    confirmButton: "Yes, cancel",
    cancelButton: "Back",
  },
  admin: languageStringsAdmin,
  campaign: languageStringsCampaign,
  subscriptions: languageStringsSubscriptions,
  contact: languageStringsContact,
  popups: languageStringsPopups,
};
